/** @returns {'admin'|'sub admin'|null} */
export function getRoleLabel(user) {
  if (!user?.role) return null;
  if (typeof user.role === "string") return user.role;
  return user.role.role ?? null;
}

export function normalizeRole(role, user) {
  if (role && typeof role === "object" && role.role) {
    return { is_staff: Boolean(role.is_staff), role: role.role };
  }
  if (typeof role === "string") {
    return { is_staff: true, role };
  }
  // Legacy payloads (before role-only auth shape)
  if (user?.is_admin) return { is_staff: true, role: "admin" };
  if (user?.is_sub_admin) return { is_staff: true, role: "sub admin" };
  return null;
}

/** Minimal user object stored in auth state / localStorage. */
export function normalizeAuthUser(user) {
  if (!user) return null;
  const role = normalizeRole(user.role, user);
  const staffUser = { role };
  const core = {
    id: user.id,
    email: user.email,
    name: user.name || user.full_name,
    is_verified: user.is_verified,
    role,
    // Legacy flags — UI still gates bookmark/edit and staff actions on these
    is_admin: canAccessAdminPanel(staffUser),
    is_sub_admin: isSubAdmin(staffUser),
  };
  // Preserve profile fields in Redux when present (omitted from localStorage save).
  const profileKeys = [
    "profession_id",
    "profession_name",
    "specialties",
    "work_environment",
    "date_of_exercise_start",
    "followed_institutions",
    "years_of_experience",
    "first_name",
    "last_name",
    "full_name",
    "profile_picture",
    "country_code",
    "phone_number",
  ];
  const profile = {};
  for (const key of profileKeys) {
    if (user[key] !== undefined && user[key] !== null) {
      profile[key] = user[key];
    }
  }
  return { ...core, ...profile };
}

export function isFullAdmin(user) {
  return getRoleLabel(user) === "admin";
}

export function isSubAdmin(user) {
  return getRoleLabel(user) === "sub admin";
}

export function canAccessAdminPanel(user) {
  const label = getRoleLabel(user);
  return label === "admin" || label === "sub admin";
}

export function canDelete(user) {
  return isFullAdmin(user);
}

export function canPublish(user) {
  return isFullAdmin(user);
}

export function canManageSubAdmins(user) {
  return isFullAdmin(user);
}

export function getPostLoginPath(user) {
  return canAccessAdminPanel(user) ? "/admin/dashboard" : "/home";
}

/** JWT payload check for admin WebSocket. */
export function tokenHasAdminAccess(decodedPayload) {
  if (!decodedPayload) return false;
  const role = decodedPayload.role;
  if (typeof role === "string") {
    return role === "admin" || role === "sub admin";
  }
  if (role?.role) {
    return role.role === "admin" || role.role === "sub admin";
  }
  return Boolean(decodedPayload.is_admin || decodedPayload.is_sub_admin);
}
